import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  IconButton,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import EventIcon from '@mui/icons-material/Event';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import RecordViewerService from '../../../services/RecordViewerService';
import supabaseService from '../../../services/SupabaseService';
import firebaseService from '../services/FirebaseService';
import { useDraft } from '../contexts/DraftContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import HeroImage from './HeroImage';

const Container = styled(Box)`
  padding: 0;
  width: 100%;
  background-color: #0a0a0a;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Header = styled(Box)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #0a0a0a;
  border-bottom: 2px solid #333;
  flex-shrink: 0;
`;

const Title = styled(Typography)`
  color: #00d4ff;
  font-weight: bold;
  font-size: 32px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ControlsRow = styled(Box)`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const StatsChip = styled(Chip)`
  background-color: #1a1a1a !important;
  color: #00d4ff !important;
  border: 1px solid #00d4ff !important;
  margin-left: 8px;
`;

// Horizontal Canvas Container (main scrolling area)
const HorizontalCanvas = styled.div`
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 48px;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #333 #1a1a1a;

  &::-webkit-scrollbar {
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 6px;

    &:hover {
      background: #444;
    }
  }
`;

// Team Row Container (horizontal teams for "By Tournament" mode OR vertical teams for "By Team" mode)
const TeamRowContainer = styled.div`
  display: flex;
  flex-direction: ${props => props.$mode === 'team' ? 'column' : 'row'};
  gap: ${props => props.$mode === 'team' ? '48px' : '24px'};
  min-height: ${props => props.$mode === 'team' ? 'auto' : '100%'};
`;

// Team Section (one team with all its tournaments/drafts)
const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
`;

// Team Group Header (sticky when scrolling vertically in "By Team" mode)
const TeamGroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border-left: 4px solid #00d4ff;
  border-radius: 4px;
  position: sticky;
  top: 0;
  z-index: 10;
  flex-shrink: 0;
`;

// Tournament Columns Container (horizontal scrolling of tournament columns)
const TournamentColumnsContainer = styled.div`
  display: flex;
  gap: 24px;
  overflow-x: auto;
  padding-bottom: 8px;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #333 #1a1a1a;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 4px;

    &:hover {
      background: #444;
    }
  }
`;

// Tournament Column (7 draft boards wide: 7*280 + 6*16 gaps + 24 card padding + 32 col padding = 2112px)
const TournamentColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 2112px;
  min-width: 2112px;
  flex-shrink: 0;
  gap: 16px;
  background: linear-gradient(to bottom, rgba(22, 25, 38, 0.5), rgba(11, 13, 20, 0.5));
  border-radius: 12px;
  padding: 16px;
  border: 2px solid #333;
`;

// Tournament Header (sticky at top of column)
const TournamentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  position: sticky;
  top: 0;
  z-index: 9;
  flex-shrink: 0;
`;

// Drafts Container (horizontal scrolling within tournament column)
const DraftsHorizontalContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #444 #1a1a1a;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #444;
    border-radius: 4px;

    &:hover {
      background: #555;
    }
  }
`;

// Draft Card Container (vertical stack of scenario boards)
const DraftCardHorizontal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  background: linear-gradient(to bottom, rgba(22, 25, 38, 0.9), rgba(11, 13, 20, 0.9));
  border: 2px solid #333;
  border-radius: 12px;
  padding: 12px;
  transition: all 0.2s;

  &:hover {
    border-color: #00d4ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 212, 255, 0.2);
  }
`;

// Draft Group Styling (OLD - keeping for fallback/compatibility)
const GroupContainer = styled.div`
  margin-bottom: 32px;
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  border-left: 4px solid #00d4ff;
  margin-bottom: 16px;
  border-radius: 4px;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const GroupIcon = styled.div`
  color: #00d4ff;
  display: flex;
  align-items: center;
`;

const GroupTitle = styled(Typography)`
  color: #00d4ff;
  font-weight: bold;
  font-size: 20px;
  flex: 1;
`;

const GroupSubtitle = styled(Typography)`
  color: #888;
  font-size: 14px;
`;

// Subgroup (team within tournament or tournament within team)
const SubGroupContainer = styled.div`
  margin-bottom: 24px;
`;

const SubGroupHeader = styled.div`
  padding: 8px 12px;
  margin-bottom: 12px;
  margin-top: 8px;
  border-left: 3px solid ${props => props.$color || '#4ade80'};
  background: rgba(255, 255, 255, 0.02);
  border-radius: 2px;
  position: sticky;
  top: 64px;
  z-index: 9;
  background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
`;

const SubGroupTitle = styled(Typography)`
  color: ${props => props.$color || '#4ade80'};
  font-weight: bold;
  font-size: 16px;
`;

// Individual Draft Card
const DraftCard = styled.div`
  background: linear-gradient(to bottom, rgba(22, 25, 38, 0.9), rgba(11, 13, 20, 0.9));
  border: 2px solid #333;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: #00d4ff;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 212, 255, 0.2);
  }
`;

const DraftCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #333;
`;

const DraftTitle = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
`;

const DraftMeta = styled.div`
  color: #888;
  font-size: 12px;
`;

const LoadDraftButton = styled(Button)`
  && {
    background: #00d4ff;
    color: #000;
    font-weight: bold;
    font-size: 11px;
    padding: 4px 12px;

    &:hover {
      background: #00b8d4;
    }
  }
`;

const DeleteDraftButton = styled(Button)`
  && {
    background: #d32f2f;
    color: #fff;
    font-weight: bold;
    font-size: 11px;
    padding: 4px 12px;

    &:hover {
      background: #b71c1c;
    }
  }
`;

// Scenario Boards Container (horizontal scroll for scenarios)
const ScenariosContainer = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: #333 #1a1a1a;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333;
    border-radius: 4px;

    &:hover {
      background: #444;
    }
  }
`;

// DraftBoard-style components (matching exact style from DraftBoard.js)
const DraftBoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  padding: 12px;
  border-radius: 12px;
  width: 280px;
  min-width: 280px;
  max-width: 280px;
  flex-shrink: 0;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border: 3px solid ${props => props.$pickMode === 'first' ? '#1de9b6' : '#61dafb'};
`;

const ScenarioTitleRow = styled.div`
  text-align: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #333;
`;

const ScenarioTitleText = styled.div`
  color: #fff;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const PickModeLabel = styled.div`
  color: ${props => props.$pickMode === 'first' ? '#1de9b6' : '#61dafb'};
  font-size: 12px;
  font-weight: bold;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  align-items: center;
`;

const TeamHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-align: center;
  width: 50%;
  text-shadow: 0 0 8px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
`;

const PicksContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  gap: 16px;
  background: linear-gradient(to bottom, rgba(20, 22, 34, 0.8), rgba(14, 16, 26, 0.8));
  padding: 12px 0;
  border-radius: 8px;
`;

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => {
    // When not swapped: Radiant (left) aligns right, Dire (right) aligns left
    // When swapped: Dire (left) aligns right, Radiant (right) aligns left
    const isRadiant = props.$team === 'Radiant';
    const isSwapped = props.$swapped || false;

    // Radiant is normally on left, Dire on right
    // When swapped, positions flip
    const isOnLeft = isSwapped ? !isRadiant : isRadiant;

    return isOnLeft ? 'flex-end' : 'flex-start';
  }};
  gap: 12px;
`;

// MLBB: large pick boxes (80px), small ban boxes (60px) — all circular
const LARGE_BOX_SEQUENCES = [8, 9, 13, 14, 15, 16, 17, 18, 20, 23, 24, 25];
const getBoxSize = (position) => LARGE_BOX_SEQUENCES.includes(position) ? '80px' : '60px';

// MLBB position label mapping
const getDisplayPosition = (position) => {
  const m = { 4:3, 3:4, 7:5, 5:6, 14:7, 13:8, 16:9, 15:10, 18:11, 17:12, 26:13, 27:14, 21:15, 22:16, 24:17, 23:18, 20:19, 25:20 };
  return m[position] || position;
};

const PickBox = styled.div`
  border: 1px solid #444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => getBoxSize(props.$position)};
  width: ${props => getBoxSize(props.$position)};
  position: relative;
  background-color: #111215;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  padding: 0;

  &.filled {
    background-color: ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  }
`;

// Position label colors matching MLBB DraftBoard (ban=red, pick=green)
const PositionLabel = styled.div`
  position: absolute;
  top: 4px;
  left: 4px;
  font-size: 12px;
  font-weight: bold;
  color: ${props => {
    const displayPos = getDisplayPosition(props.$position);
    if ([1,2,3,4,5,6,13,14,15,16].includes(displayPos)) return '#ff0000';
    if ([7,8,9,10,11,12,17,18,19,20].includes(displayPos)) return '#00ff00';
    return 'rgba(255,255,255,0.8)';
  }};
  background-color: rgba(0, 0, 0, 0.5);
  padding: 1px 3px;
  border-radius: 2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  z-index: 2;
`;

const HeroContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;

  > div {
    width: 100%;
    height: 100%;
  }
`;

const BanCross = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6b6b;
  font-weight: bold;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.9);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    z-index: -1;
  }
`;

const EmptyState = styled(Box)`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const PaginationContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background-color: #0a0a0a;
  border-top: 2px solid #333;
  flex-shrink: 0;
`;

const PaginationButton = styled(Button)`
  && {
    background: #00d4ff;
    color: #000;
    font-weight: bold;
    padding: 10px 24px;
    font-size: 14px;
    border-radius: 8px;

    &:hover {
      background: #00b8d4;
    }

    &:disabled {
      background: #333;
      color: #666;
      cursor: not-allowed;
    }
  }
`;

const PageInfo = styled(Typography)`
  color: #00d4ff;
  font-size: 16px;
  font-weight: 500;
  min-width: 150px;
  text-align: center;
`;

const SearchActiveNotice = styled(Box)`
  padding: 12px 24px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #000;
  text-align: center;
  font-weight: 600;
  border-radius: 8px;
  margin: 0 24px 16px;
`;

const CloseButton = styled(IconButton)`
  position: fixed !important;
  top: 16px;
  right: 16px;
  background-color: #1a1a1a !important;
  color: #00d4ff !important;
  z-index: 1000;

  &:hover {
    background-color: #2a2a2a !important;
  }
`;

const VIEW_MODES = {
  TOURNAMENT: 'tournament',
  TEAM: 'team'
};

function RecordViewer() {
  const [viewMode, setViewMode] = useState(VIEW_MODES.TEAM);
  const [teamSearch, setTeamSearch] = useState('');
  const [tournamentSearch, setTournamentSearch] = useState('');
  const [allDrafts, setAllDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Team-based pagination: one team per page
  const [teamList, setTeamList] = useState([]); // [{teamName, latestUpdatedAt}]
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);

  // Lazy scenario loading: cache per draftId
  // scenarioCache/loadingScenarios removed — scenarios now loaded upfront in getRecordViewerPage

  // Migration state
  const [supabaseDraftCount, setSupabaseDraftCount] = useState(null);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState(null);

  // Debounce ref for search inputs
  // Skip the debounced search effect on initial mount (mount effect already loads page 1)
  const isInitialMount = useRef(true);

  const { importDraftData } = useDraft();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Core loader — fetches full drafts (with scenarios) for a team or search
  const loadPage = useCallback(async (page, ts, tos, exactTeamName = null) => {
    setLoading(true);
    try {
      if (!currentUser) {
        setAllDrafts([]);
        setTotalCount(0);
        return;
      }

      const { drafts, totalCount: count } = await supabaseService.getRecordViewerPage(currentUser.uid, {
        teamSearch: ts,
        tournamentSearch: tos,
        exactTeamName,
        page,
        pageSize: exactTeamName ? 500 : 50,
      });

      setAllDrafts(drafts);
      setTotalCount(count);
    } catch (error) {
      console.error('❌ RecordViewer: Error loading from Supabase:', error);
      setAllDrafts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // On mount / user change: load team list then show first team
  useEffect(() => {
    if (!currentUser) return;

    supabaseService.getDraftCount(currentUser.uid)
      .then(count => setSupabaseDraftCount(count))
      .catch(() => setSupabaseDraftCount(0));

    supabaseService.getTeamList(currentUser.uid).then(list => {
      setTeamList(list);
      setCurrentTeamIndex(0);
      if (list.length > 0) {
        loadPage(1, '', '', list[0].teamName);
      } else {
        loadPage(1, '', '');
      }
    });
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trigger search manually (on Enter key or clear button)
  const triggerSearch = useCallback((ts, tos) => {
    if (ts.trim() || tos.trim()) {
      loadPage(1, ts, tos);
    } else {
      const teamName = teamList[currentTeamIndex]?.teamName;
      if (teamName) loadPage(1, '', '', teamName);
      else loadPage(1, '', '');
    }
  }, [loadPage, teamList, currentTeamIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Escape key to close the viewer
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        navigate('/drafts');
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [navigate]);

  const groupedData = useMemo(() => {
    return viewMode === VIEW_MODES.TOURNAMENT
      ? RecordViewerService.groupByTournament(allDrafts)
      : RecordViewerService.groupByTeam(allDrafts);
  }, [allDrafts, viewMode]);

  const paginatedGroups = groupedData;
  const isSearchActive = teamSearch.trim() !== '' || tournamentSearch.trim() !== '';
  const totalGroups = groupedData.length;

  // Statistics from loaded metadata
  const stats = RecordViewerService.getStatistics(allDrafts);

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };


  // Handle load draft - fetch full draft from Supabase, then import into editor
  const handleLoadDraft = async (draft, e) => {
    if (e) e.stopPropagation();
    try {
      const fullDraft = await supabaseService.getDraft(draft.id);
      if (!fullDraft) throw new Error('Draft not found');
      await importDraftData(fullDraft, true);
      navigate('/drafts');
    } catch (error) {
      console.error('Error loading draft:', error);
      alert('Failed to load draft: ' + error.message);
    }
  };

  // Handle delete draft
  const handleDeleteDraft = async (draft, e) => {
    if (e) e.stopPropagation();

    const confirmDelete = window.confirm(
      `Are you sure you want to delete this draft?\n\n${draft.teamName || 'Untitled'} - ${draft.tournament || 'No Tournament'}\n${draft.stage || ''} ${draft.date || ''}\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      await supabaseService.deleteDraft(draft.id, currentUser?.uid);
      const teamName = isSearchActive ? null : teamList[currentTeamIndex]?.teamName;
      await loadPage(1, isSearchActive ? teamSearch : '', isSearchActive ? tournamentSearch : '', teamName || null);
    } catch (error) {
      console.error('❌ Error deleting draft:', error);
      alert('Failed to delete draft: ' + error.message);
    }
  };

  // Team navigation (prev/next team when not searching)
  const handlePrevTeam = () => {
    if (currentTeamIndex > 0) {
      const newIndex = currentTeamIndex - 1;
      setCurrentTeamIndex(newIndex);
      loadPage(1, '', '', teamList[newIndex].teamName);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNextTeam = () => {
    if (currentTeamIndex < teamList.length - 1) {
      const newIndex = currentTeamIndex + 1;
      setCurrentTeamIndex(newIndex);
      loadPage(1, '', '', teamList[newIndex].teamName);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // One-click Firebase → Supabase migration
  const handleMigrate = async () => {
    if (!currentUser) return;
    const confirmMigrate = window.confirm(
      'This will copy all your Firebase drafts into Supabase. Continue?'
    );
    if (!confirmMigrate) return;

    setIsMigrating(true);
    setMigrateProgress({ done: 0, total: 0 });
    try {
      // Step 1: Fetch from Firebase
      console.log('🔄 Migration: fetching Firebase drafts for', currentUser.uid);
      let firebaseDrafts;
      try {
        firebaseDrafts = await firebaseService.getUserDrafts(currentUser.uid);
        console.log('🔄 Migration: got', firebaseDrafts.length, 'Firebase drafts');
      } catch (fbError) {
        console.error('❌ Migration: Firebase fetch failed:', fbError);
        throw new Error('Firebase read failed: ' + fbError.message + '\n\nMake sure you are logged in and have internet access.');
      }

      if (firebaseDrafts.length === 0) {
        alert('No Firebase drafts found to migrate.');
        return;
      }

      setMigrateProgress({ done: 0, total: firebaseDrafts.length });

      // Step 2: Upsert into Supabase one-by-one
      const draftsWithUser = firebaseDrafts.map(d => ({
        ...d,
        userId: d.userId || currentUser.uid,
        userEmail: d.userEmail || currentUser.email,
      }));

      console.log('🔄 Migration: starting Supabase upsert of', draftsWithUser.length, 'drafts');
      await supabaseService.migrateBatch(draftsWithUser, (done, total) => {
        setMigrateProgress({ done, total });
        console.log(`🔄 Migration progress: ${done}/${total}`);
      });

      setSupabaseDraftCount(firebaseDrafts.length);
      alert(`Successfully migrated ${firebaseDrafts.length} drafts to Supabase!`);
      // Reload team list and show first team
      const list = await supabaseService.getTeamList(currentUser.uid);
      setTeamList(list);
      setCurrentTeamIndex(0);
      if (list.length > 0) await loadPage(1, '', '', list[0].teamName);
      else await loadPage(1, '', '');
    } catch (error) {
      console.error('❌ Migration error:', error);
      alert('Migration failed: ' + error.message);
    } finally {
      setIsMigrating(false);
      setMigrateProgress(null);
    }
  };

  // Handle close button
  const handleClose = () => {
    navigate('/drafts');
  };

  // Render a single scenario board (MLBB layout — circular boxes, MLBB positions)
  const renderScenario = (scenario, pickMode) => {
    // MLBB column positions (matches DraftBoard.js)
    const leftPositions  = [1, 4, 7, 14, 15, 18, 27, 22, 23, 20];
    const rightPositions = [2, 3, 5, 13, 16, 17, 26, 21, 24, 25];
    const banPositions   = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 21, 22, 26, 27];

    const renderBox = (position) => {
      const pick = scenario.picks?.[position - 1];
      const isBan = banPositions.includes(position);

      return (
        <PickBox
          key={position}
          $position={position}
          $team="Radiant"
          className={pick?.hero ? 'filled' : ''}
        >
          <PositionLabel $position={position}>{getDisplayPosition(position)}</PositionLabel>
          {pick?.hero && (
            <HeroContainer>
              <HeroImage
                hero={pick.hero}
                size={LARGE_BOX_SEQUENCES.includes(position) ? 'large' : 'medium'}
                $grayscale={isBan}
              />
            </HeroContainer>
          )}
          {isBan && <BanCross>✕</BanCross>}
        </PickBox>
      );
    };

    const swapped = scenario.swapSides || false;
    const leftTeam  = swapped ? 'Dire' : 'Radiant';
    const rightTeam = swapped ? 'Radiant' : 'Dire';
    const leftCols  = swapped ? rightPositions : leftPositions;
    const rightCols = swapped ? leftPositions  : rightPositions;

    return (
      <DraftBoardContainer key={`${scenario.id}-${pickMode}`} $pickMode={pickMode}>
        <ScenarioTitleRow>
          <ScenarioTitleText>{scenario.title || 'Scenario'}</ScenarioTitleText>
          <PickModeLabel $pickMode={pickMode}>
            {pickMode === 'first' ? '1st Pick' : '2nd Pick'}
          </PickModeLabel>
        </ScenarioTitleRow>

        <TeamsContainer>
          <TeamHeader $team={leftTeam}>{leftTeam}</TeamHeader>
          <TeamHeader $team={rightTeam}>{rightTeam}</TeamHeader>
        </TeamsContainer>

        <PicksContainer>
          <TeamColumn $team={leftTeam} $swapped={swapped}>
            {leftCols.map(pos => renderBox(pos))}
          </TeamColumn>
          <TeamColumn $team={rightTeam} $swapped={swapped}>
            {rightCols.map(pos => renderBox(pos))}
          </TeamColumn>
        </PicksContainer>
      </DraftBoardContainer>
    );
  };

  // Render a single draft with all its scenarios
  // Format date as DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'No Date') return 'No Date';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Invalid date, return as-is

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  const renderDraft = (draft, draftIndex) => {
    const firstPickScenarios = draft.scenariosFirstPick || [];
    const secondPickScenarios = draft.scenariosSecondPick || [];
    const layoutVersion = draft.layoutVersion || null; // null = old layout (pre-4.0)

    return (
      <DraftCard key={`draft-${draftIndex}`}>
        <DraftCardHeader>
          <div>
            <DraftTitle>
              {draft.stage || 'No Stage'} - {formatDate(draft.date)}
            </DraftTitle>
            <DraftMeta>
              {firstPickScenarios.length} scenarios (1st pick) • {secondPickScenarios.length} scenarios (2nd pick)
            </DraftMeta>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <LoadDraftButton
              size="small"
              onClick={(e) => handleLoadDraft(draft, e)}
            >
              Load Draft
            </LoadDraftButton>
            <DeleteDraftButton
              size="small"
              onClick={(e) => handleDeleteDraft(draft, e)}
            >
              🗑 Delete
            </DeleteDraftButton>
          </div>
        </DraftCardHeader>

        <ScenariosContainer>
          {firstPickScenarios.map(scenario => renderScenario(scenario, 'first'))}
          {secondPickScenarios.map(scenario => renderScenario(scenario, 'second'))}
        </ScenariosContainer>
      </DraftCard>
    );
  };

  // Render a single draft with horizontal layout (all scenarios visible horizontally)
  const renderDraftHorizontal = (draft, draftIndex) => {
    const firstPickScenarios = draft.scenariosFirstPick || [];
    const secondPickScenarios = draft.scenariosSecondPick || [];
    const layoutVersion = draft.layoutVersion || null;
    const hasScenarios = firstPickScenarios.length > 0 || secondPickScenarios.length > 0;

    return (
      <DraftCardHorizontal key={`draft-${draftIndex}`}>
        {/* Draft metadata header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '8px',
          borderBottom: '1px solid #333',
          marginBottom: '8px'
        }}>
          <div>
            <Typography style={{ color: '#ffffff', fontSize: '14px', fontWeight: 500 }}>
              {draft.stage || 'No Stage'} - {formatDate(draft.date)}
            </Typography>
            <Typography style={{ color: '#888', fontSize: '11px' }}>
              {`${firstPickScenarios.length} scenarios (1st) • ${secondPickScenarios.length} scenarios (2nd)`}
            </Typography>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <LoadDraftButton
              size="small"
              onClick={(e) => handleLoadDraft(draft, e)}
            >
              Load
            </LoadDraftButton>
            <DeleteDraftButton
              size="small"
              onClick={(e) => handleDeleteDraft(draft, e)}
            >
              🗑
            </DeleteDraftButton>
          </div>
        </div>

        {hasScenarios && (
          <ScenariosContainer>
            {firstPickScenarios.map(scenario => renderScenario(scenario, 'first', layoutVersion, draft.date))}
            {secondPickScenarios.map(scenario => renderScenario(scenario, 'second', layoutVersion, draft.date))}
          </ScenariosContainer>
        )}
      </DraftCardHorizontal>
    );
  };

  // Render tournament-grouped view (NEW HORIZONTAL LAYOUT)
  const renderTournamentView = (groups = paginatedGroups) => {
    if (groups.length === 0) {
      return (
        <EmptyState>
          <Typography variant="h6" color="inherit">
            No drafts found
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ mt: 1 }}>
            {(teamSearch || tournamentSearch) ? 'Try adjusting your search' : 'Start creating drafts to see them here'}
          </Typography>
        </EmptyState>
      );
    }

    // Render: Tournaments stacked vertically → Each tournament has teams as columns horizontally
    return (
      <TeamRowContainer $mode="team">
        {groups.map((tournamentGroup, tIndex) => (
          <TeamSection key={`tournament-${tIndex}`}>
            <TeamGroupHeader>
              <GroupIcon>
                <EventIcon />
              </GroupIcon>
              <div style={{ flex: 1 }}>
                <GroupTitle>{tournamentGroup.tournament}</GroupTitle>
                <GroupSubtitle>
                  {tournamentGroup.teams.length} team{tournamentGroup.teams.length !== 1 ? 's' : ''}
                </GroupSubtitle>
              </div>
            </TeamGroupHeader>

            {/* Team columns for this tournament (horizontal scroll) */}
            <TournamentColumnsContainer>
              {tournamentGroup.teams.map((teamGroup, teamIndex) => (
                <TournamentColumn key={`team-${teamIndex}`}>
                  <TournamentHeader>
                    <GroupWorkIcon style={{ color: '#4ade80' }} />
                    <div style={{ flex: 1 }}>
                      <SubGroupTitle $color="#4ade80">
                        {teamGroup.team}
                      </SubGroupTitle>
                      <Typography variant="caption" style={{ color: '#888' }}>
                        {teamGroup.drafts.length} draft{teamGroup.drafts.length !== 1 ? 's' : ''}
                      </Typography>
                    </div>
                  </TournamentHeader>

                  {/* Drafts within this team (horizontal scroll) */}
                  <DraftsHorizontalContainer>
                    {teamGroup.drafts.map((draft, draftIndex) => renderDraftHorizontal(draft, draftIndex))}
                  </DraftsHorizontalContainer>
                </TournamentColumn>
              ))}
            </TournamentColumnsContainer>
          </TeamSection>
        ))}
      </TeamRowContainer>
    );
  };

  // Render team-grouped view (NEW HORIZONTAL LAYOUT)
  const renderTeamView = (groups = paginatedGroups) => {
    if (groups.length === 0) {
      return (
        <EmptyState>
          <Typography variant="h6" color="inherit">
            No drafts found
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ mt: 1 }}>
            {(teamSearch || tournamentSearch) ? 'Try adjusting your search' : 'Start creating drafts to see them here'}
          </Typography>
        </EmptyState>
      );
    }

    // Render: Teams stacked vertically → Each team has tournaments horizontally → Each tournament has drafts
    return (
      <TeamRowContainer $mode="team">
        {groups.map((teamGroup, tIndex) => (
          <TeamSection key={`team-${tIndex}`}>
            <TeamGroupHeader>
              <GroupIcon>
                <GroupWorkIcon />
              </GroupIcon>
              <div style={{ flex: 1 }}>
                <GroupTitle>{teamGroup.team}</GroupTitle>
                <GroupSubtitle>
                  {teamGroup.tournaments.length} tournament{teamGroup.tournaments.length !== 1 ? 's' : ''}
                </GroupSubtitle>
              </div>
            </TeamGroupHeader>

            {/* Tournament columns for this team (horizontal scroll) */}
            <TournamentColumnsContainer>
              {teamGroup.tournaments.map((tournamentGroup, tourIndex) => (
                <TournamentColumn key={`tournament-${tourIndex}`}>
                  <TournamentHeader>
                    <EventIcon style={{ color: '#f59e0b' }} />
                    <div style={{ flex: 1 }}>
                      <SubGroupTitle $color="#f59e0b">
                        {tournamentGroup.tournament}
                      </SubGroupTitle>
                      <Typography variant="caption" style={{ color: '#888' }}>
                        {tournamentGroup.drafts.length} draft{tournamentGroup.drafts.length !== 1 ? 's' : ''}
                      </Typography>
                    </div>
                  </TournamentHeader>

                  {/* Drafts within this tournament (horizontal scroll) */}
                  <DraftsHorizontalContainer>
                    {tournamentGroup.drafts.map((draft, draftIndex) => renderDraftHorizontal(draft, draftIndex))}
                  </DraftsHorizontalContainer>
                </TournamentColumn>
              ))}
            </TournamentColumnsContainer>
          </TeamSection>
        ))}
      </TeamRowContainer>
    );
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <CircularProgress sx={{ color: '#00d4ff' }} />
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <CloseButton onClick={handleClose}>
        <CloseIcon />
      </CloseButton>

      <Header>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Title>Draft Records</Title>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <StatsChip label={`${totalCount} Drafts`} size="small" />
            <StatsChip label={`${stats.totalTournaments} Tournaments`} size="small" />
            <StatsChip label={`${teamList.length || stats.totalTeams} Teams`} size="small" />
          </Box>
          {/* Migration button — shown when Supabase is empty */}
          {supabaseDraftCount === 0 && currentUser && (
            <Button
              size="small"
              disabled={isMigrating}
              onClick={handleMigrate}
              sx={{
                background: '#f59e0b',
                color: '#000',
                fontWeight: 'bold',
                fontSize: '11px',
                px: 2,
                '&:hover': { background: '#d97706' },
                '&:disabled': { background: '#555', color: '#999' }
              }}
            >
              {isMigrating
                ? `Migrating… ${migrateProgress?.done ?? 0}/${migrateProgress?.total ?? '?'}`
                : '⬆ Migrate from Firebase'}
            </Button>
          )}
        </Box>

        <ControlsRow>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#888',
                borderColor: '#333',
                '&.Mui-selected': {
                  backgroundColor: '#00d4ff',
                  color: '#000',
                  '&:hover': {
                    backgroundColor: '#00b8d4',
                  }
                }
              }
            }}
          >
            <ToggleButton value={VIEW_MODES.TEAM}>
              <GroupWorkIcon sx={{ mr: 1 }} />
              By Team
            </ToggleButton>
            <ToggleButton value={VIEW_MODES.TOURNAMENT}>
              <EventIcon sx={{ mr: 1 }} />
              By Tournament
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Search by Team"
            placeholder="Enter team name..."
            value={teamSearch}
            onChange={(e) => setTeamSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(teamSearch, tournamentSearch); }}
            size="small"
            sx={{
              flex: 1,
              minWidth: '200px',
              '& .MuiInputBase-root': {
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#333',
              },
              '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00d4ff',
              },
              '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00d4ff',
              },
              '& .MuiInputLabel-root': {
                color: '#999',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#00d4ff',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
              endAdornment: teamSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => { setTeamSearch(''); triggerSearch('', tournamentSearch); }}
                    sx={{ color: '#999' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Search by Tournament"
            placeholder="Enter tournament name..."
            value={tournamentSearch}
            onChange={(e) => setTournamentSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') triggerSearch(teamSearch, tournamentSearch); }}
            size="small"
            sx={{
              flex: 1,
              minWidth: '200px',
              '& .MuiInputBase-root': {
                backgroundColor: '#1a1a1a',
                color: '#ffffff',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#333',
              },
              '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00d4ff',
              },
              '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00d4ff',
              },
              '& .MuiInputLabel-root': {
                color: '#999',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#00d4ff',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888' }} />
                </InputAdornment>
              ),
              endAdornment: tournamentSearch && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => { setTournamentSearch(''); triggerSearch(teamSearch, ''); }}
                    sx={{ color: '#999' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </ControlsRow>
      </Header>

      <HorizontalCanvas>
        {/* Search active notice */}
        {isSearchActive && (
          <SearchActiveNotice>
            <Typography variant="body1" fontWeight="bold">
              🔍 Showing {totalCount} matching draft{totalCount !== 1 ? 's' : ''} across {totalGroups} {viewMode === VIEW_MODES.TOURNAMENT ? 'tournament' : 'team'}{totalGroups !== 1 ? 's' : ''}
            </Typography>
          </SearchActiveNotice>
        )}

        {/* Render groups from current server page */}
        {viewMode === VIEW_MODES.TOURNAMENT
          ? renderTournamentView(paginatedGroups)
          : renderTeamView(paginatedGroups)}
      </HorizontalCanvas>

      {/* Team navigation — hidden during search */}
      {!isSearchActive && teamList.length > 1 && (
        <PaginationContainer>
          <PaginationButton
            disabled={currentTeamIndex === 0}
            onClick={handlePrevTeam}
          >
            ← Previous
          </PaginationButton>

          <PageInfo>
            Team {currentTeamIndex + 1} of {teamList.length}
            <br />
            <span style={{ fontSize: '12px', color: '#00d4ff' }}>
              {teamList[currentTeamIndex]?.teamName}
            </span>
          </PageInfo>

          <PaginationButton
            disabled={currentTeamIndex >= teamList.length - 1}
            onClick={handleNextTeam}
          >
            Next →
          </PaginationButton>
        </PaginationContainer>
      )}
    </Container>
  );
}

export default RecordViewer;
